import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { IllustrationUpload } from '../components/ui/IllustrationUpload';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FactionSelect } from '../components/ui/FactionSelect';
import { isGameMaster, getNPCStatusClass, getInitials, cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Save,
  Trash2,
  Loader2,
  Eye,
  EyeOff,
  User
} from 'lucide-react';
import { LoreLinksPanel } from '../components/lore/LoreLinksPanel';
import { MentionTextarea } from '../components/mentions/MentionTextarea';
import { MentionText } from '../components/mentions/MentionText';
import { syncEntityMentions } from '../lib/mentions';

const API_URL = '/api';

const NPC_STATUSES = ['alive', 'dead', 'missing', 'hostile', 'ally'];

export default function NPCDetailPage() {
  const { campaignId, npcId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  const [npc, setNpc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    fetchNpc();
  }, [npcId]);

  const fetchNpc = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/npcs/${npcId}`);
      setNpc(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error(t('error_generic', language));
      navigate(`/campaigns/${campaignId}/npcs`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(`${API_URL}/npcs/${npcId}`, editData);
      setNpc(response.data);
      setEditMode(false);
      toast.success(t('success_saved', language));
      syncEntityMentions({
        campaignId,
        sourceType: 'npc',
        sourceId: npcId,
        sourceFields: {
          public_info: editData.public_info || '',
          appearance: editData.appearance || '',
          voice_mannerisms: editData.voice_mannerisms || '',
          secrets: editData.secrets || '',
          what_they_know: editData.what_they_know || '',
          what_they_want: editData.what_they_want || '',
          what_they_hide: editData.what_they_hide || ''
        }
      }).catch(() => {});
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(language === 'fr' ? 'Êtes-vous sûr de vouloir supprimer ce PNJ ?' : 'Are you sure you want to delete this NPC?')) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/npcs/${npcId}`);
      toast.success(t('success_deleted', language));
      navigate(`/campaigns/${campaignId}/npcs`);
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !npc) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in" data-testid="npc-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/campaigns/${campaignId}/npcs`)}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back', language)}
        </Button>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex items-start gap-5">
            <IllustrationUpload
              imageUrl={editMode ? (editData.image_url || '') : (npc.image_url || '')}
              onImageChange={(url) => {
                setEditData(prev => ({ ...prev, image_url: url }));
                if (!editMode) {
                  setNpc(prev => ({ ...prev, image_url: url }));
                }
              }}
              entityType="npc"
              entityId={npcId}
              campaignId={campaignId}
              language={language}
              editable={isMJ}
              size="lg"
              shape="rounded"
              fallbackText={getInitials(npc.name)}
              className="shrink-0"
            />
            <div>
              <div className="flex items-center gap-3 mb-1">
                {editMode ? (
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                    className="font-cinzel text-2xl font-bold"
                    data-testid="name-input"
                  />
                ) : (
                  <h1 className="font-cinzel text-3xl font-bold">{npc.name}</h1>
                )}
                <span className={cn("status-chip", getNPCStatusClass(npc.status))}>
                  {t(`npc_status_${npc.status}`, language)}
                </span>
              </div>
              {npc.alias && (
                <p className="text-lg text-muted-foreground italic mb-2">"{npc.alias}"</p>
              )}
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {npc.race && <span>{npc.race}</span>}
                {npc.role && <><span>•</span><span>{npc.role}</span></>}
                {npc.faction && <><span>•</span><span>{npc.faction}</span></>}
              </div>
              {isMJ && (
                <div className="mt-2">
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-fit",
                    npc.visibility === 'mj_only' ? 'bg-magic/10 text-magic' : 'bg-success/10 text-success'
                  )}>
                    {npc.visibility === 'mj_only' ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {t(`visibility_${npc.visibility}`, language)}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {isMJ && (
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditData(npc); }}>
                    {t('cancel', language)}
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {t('save', language)}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-btn">
                    {t('edit', language)}
                  </Button>
                  <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-btn">
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    {t('delete', language)}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Edit Mode Controls */}
        {editMode && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('npc_alias', language)}</Label>
              <Input
                value={editData.alias || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, alias: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('npc_race', language)}</Label>
              <Input
                value={editData.race || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, race: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('npc_role', language)}</Label>
              <Input
                value={editData.role || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, role: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('npc_faction', language)}</Label>
              <FactionSelect
                value={editData.faction || ''}
                onChange={(val) => setEditData(prev => ({ ...prev, faction: val }))}
                campaignId={campaignId}
                language={language}
                placeholder={language === 'fr' ? 'Les Harpers, La Guilde...' : 'The Harpers, The Guild...'}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('npc_status', language)}</Label>
              <Select value={editData.status} onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NPC_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>
                      {t(`npc_status_${status}`, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('visibility', language)}</Label>
              <Select value={editData.visibility} onValueChange={(v) => setEditData(prev => ({ ...prev, visibility: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mj_only">{t('visibility_mj_only', language)}</SelectItem>
                  <SelectItem value="players">{t('visibility_players', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Content Tabs */}
        <Tabs defaultValue="public" className="space-y-6">
          <TabsList>
            <TabsTrigger value="public" data-testid="tab-public">
              {language === 'fr' ? 'Informations publiques' : 'Public Info'}
            </TabsTrigger>
            <TabsTrigger value="description" data-testid="tab-description">
              {language === 'fr' ? 'Description' : 'Description'}
            </TabsTrigger>
            {isMJ && (
              <TabsTrigger value="secrets" data-testid="tab-secrets">
                {language === 'fr' ? 'Secrets (MJ)' : 'Secrets (GM)'}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Public Info Tab */}
          <TabsContent value="public" className="space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>{t('npc_public_info', language)}</CardTitle>
                <CardDescription>
                  {language === 'fr' ? 'Ce que les joueurs savent' : 'What players know'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <MentionTextarea
                    campaignId={campaignId}
                    language={language}
                    value={editData.public_info || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, public_info: e.target.value }))}
                    rows={6}
                    data-testid="public-info-input"
                  />
                ) : (
                  <MentionText
                    text={npc.public_info || (language === 'fr' ? 'Aucune information publique.' : 'No public information.')}
                    campaignId={campaignId}
                    language={language}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Description Tab */}
          <TabsContent value="description" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('npc_appearance', language)}</CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={editData.appearance || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, appearance: e.target.value }))}
                      rows={4}
                      data-testid="appearance-input"
                    />
                  ) : (
                    <MentionText
                      text={npc.appearance || (language === 'fr' ? 'Non décrite.' : 'Not described.')}
                      campaignId={campaignId}
                      language={language}
                    />
                  )}
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('npc_voice', language)}</CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={editData.voice_mannerisms || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, voice_mannerisms: e.target.value }))}
                      rows={4}
                      data-testid="voice-input"
                    />
                  ) : (
                    <MentionText
                      text={npc.voice_mannerisms || (language === 'fr' ? 'Non décrites.' : 'Not described.')}
                      campaignId={campaignId}
                      language={language}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Secrets Tab (MJ only) */}
          {isMJ && (
            <TabsContent value="secrets" className="space-y-6">
              <Card className="glass-card border-magic/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4 text-magic" />
                    {t('npc_secrets', language)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <MentionTextarea
                      campaignId={campaignId}
                      language={language}
                      value={editData.secrets || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, secrets: e.target.value }))}
                      rows={4}
                      data-testid="secrets-input"
                    />
                  ) : (
                    <MentionText
                      text={npc.secrets || (language === 'fr' ? 'Aucun secret.' : 'No secrets.')}
                      campaignId={campaignId}
                      language={language}
                    />
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="glass-card border-magic/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-magic" />
                      {t('npc_what_they_know', language)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {editMode ? (
                      <MentionTextarea
                        campaignId={campaignId}
                        language={language}
                        value={editData.what_they_know || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, what_they_know: e.target.value }))}
                        rows={4}
                        data-testid="what-they-know-input"
                      />
                    ) : (
                      <MentionText
                        text={npc.what_they_know || '-'}
                        campaignId={campaignId}
                        language={language}
                        className="text-sm"
                      />
                    )}
                  </CardContent>
                </Card>

                <Card className="glass-card border-magic/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-magic" />
                      {t('npc_what_they_want', language)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {editMode ? (
                      <MentionTextarea
                        campaignId={campaignId}
                        language={language}
                        value={editData.what_they_want || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, what_they_want: e.target.value }))}
                        rows={4}
                        data-testid="what-they-want-input"
                      />
                    ) : (
                      <MentionText
                        text={npc.what_they_want || '-'}
                        campaignId={campaignId}
                        language={language}
                        className="text-sm"
                      />
                    )}
                  </CardContent>
                </Card>

                <Card className="glass-card border-magic/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-magic" />
                      {t('npc_what_they_hide', language)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {editMode ? (
                      <MentionTextarea
                        campaignId={campaignId}
                        language={language}
                        value={editData.what_they_hide || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, what_they_hide: e.target.value }))}
                        rows={4}
                        data-testid="what-they-hide-input"
                      />
                    ) : (
                      <MentionText
                        text={npc.what_they_hide || '-'}
                        campaignId={campaignId}
                        language={language}
                        className="text-sm"
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Lore Links Panel */}
        <LoreLinksPanel
          entityType="npc"
          entityId={npcId}
          entityName={npc.name}
          campaignId={campaignId}
          isMJ={isMJ}
          data-testid="lore-links-panel"
        />
      </div>
    </MainLayout>
  );
}

